/**
 * ENG-6192: the viewer's Google and Microsoft accounts on Settings → Security.
 * A free provider connects through its own sign-in button; a connected one
 * disconnects after a confirmation, unless it is the only way in.
 */
import * as Sentry from '@sentry/react';
import { useRef, useState } from 'react';

import { notify } from '@/actions/ui';
import GoogleSignInButton from '@/auth/components/GoogleSignInButton/GoogleSignInButton';
import MicrosoftSignInButton from '@/auth/components/MicrosoftSignInButton/MicrosoftSignInButton';
import {
  SocialProvider,
  isGoogleSignInEnabled,
  isMicrosoftSignInEnabled,
  socialProviderName,
} from '@/auth/utils/socialSignIn';
import { socialSignInFailure } from '@/auth/utils/socialSignInFailure';
import SettingsSection from '@/components/SettingsSection';
import ConfirmDialog from '@/componentsv2/ConfirmDialog';
import { apiErrorMessage } from '@/core/api';
import { useAppDispatch } from '@/store';

import ConnectedAccountList from './ConnectedAccountList';
import { useConnectedAccounts } from './useConnectedAccounts';

function offeredProviders(): SocialProvider[] {
  return [
    ...(isGoogleSignInEnabled() ? [SocialProvider.Google] : []),
    ...(isMicrosoftSignInEnabled() ? [SocialProvider.Microsoft] : []),
  ];
}

export default function ConnectedAccounts(): JSX.Element {
  const dispatch = useAppDispatch();
  const { loading, hasPassword, identities, connect, disconnect, error } = useConnectedAccounts();
  // A provider this environment does not offer is no way in from this page.
  const providers = offeredProviders();
  const offered = identities.filter((identity) => providers.includes(identity.provider));
  const [confirming, setConfirming] = useState<SocialProvider>();
  // One change at a time: a credential arriving while another settles is dropped.
  const lock = useRef(false);
  const [changing, setChanging] = useState(false);
  const busy = loading || changing;

  async function change(provider: SocialProvider, op: () => Promise<void>, failure: string) {
    if (lock.current) return;
    lock.current = true;
    setChanging(true);
    try {
      await op();
    } catch (err) {
      // The api's own copy for a refused change (provider already connected,
      // last way in); the fallback, reported, for anything without a domain code.
      if (!socialSignInFailure(err, provider).expected) Sentry.captureException(err);
      dispatch(notify(apiErrorMessage(err, failure), 'error'));
    } finally {
      lock.current = false;
      setChanging(false);
    }
  }

  function handleCredential(provider: SocialProvider, idToken: string) {
    const name = socialProviderName(provider);
    void change(
      provider,
      () => connect(provider, idToken),
      `We couldn't connect ${name}. Please try again.`
    );
  }

  function handleProviderError(provider: SocialProvider, err: unknown) {
    Sentry.captureException(err);
    const name = socialProviderName(provider);
    dispatch(notify(`We couldn't connect ${name}. Please try again.`, 'error'));
  }

  async function handleConfirmDisconnect(provider: SocialProvider) {
    const name = socialProviderName(provider);
    await change(
      provider,
      () => disconnect(provider),
      `We couldn't disconnect ${name}. Please try again.`
    );
    setConfirming(undefined);
  }

  // Disconnect is offered without a password only beside a second provider,
  // which is then the way back in.
  function remainingSignIn(provider: SocialProvider): string {
    const other = offered.find((identity) => identity.provider !== provider);
    if (hasPassword || !other) return 'your password';
    return socialProviderName(other.provider);
  }

  function connectButton(provider: SocialProvider): JSX.Element {
    if (provider === SocialProvider.Google) {
      return (
        <GoogleSignInButton
          text="continue_with"
          disabled={busy}
          onCredential={(idToken) => handleCredential(provider, idToken)}
        />
      );
    }
    return (
      <MicrosoftSignInButton
        text="continue_with"
        disabled={busy}
        onCredential={(idToken) => handleCredential(provider, idToken)}
        onError={(err) => handleProviderError(provider, err)}
      />
    );
  }

  if (error) {
    return (
      <SettingsSection box title="Connected accounts">
        <p className="text-on-surface-secondary">
          We could not load your connected accounts. Reload the page to try again.
        </p>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection box title="Connected accounts">
      {/* A change reads the list again while `changing`: the rows stay meanwhile. */}
      {loading && !changing ? (
        <p className="text-on-surface-secondary">Loading…</p>
      ) : (
        <ConnectedAccountList
          providers={providers}
          identities={offered}
          hasPassword={hasPassword}
          busy={busy}
          connectButton={connectButton}
          onDisconnect={setConfirming}
        />
      )}
      {confirming && (
        <ConfirmDialog
          open
          onCancel={() => {
            if (!changing) setConfirming(undefined);
          }}
          onConfirm={() => void handleConfirmDisconnect(confirming)}
          dialogTitle={`Disconnect ${socialProviderName(confirming)}?`}
          message={`You can still sign in with ${remainingSignIn(confirming)}.`}
          confirmButtonLabel="Disconnect"
          confirmButtonProps={{ disabled: changing }}
          cancelButtonProps={{ disabled: changing }}
        />
      )}
    </SettingsSection>
  );
}
